import { Loader2, ListTree, RefreshCw } from "lucide-react";
import { labelForEvent } from "../../lib/groupEventLabels";
import { Icon } from "../msd/Icon";

// Who did it shows in the icon's colour; system events stay faint like the
// prototype's.
const ACTOR_COLOR = {
  staff: "#3A5F94",
  customer: "#F79009",
};

// event_type -> the Service Desk icon drawn beside it.
export const EVENT_ICON = {
  group_created: "people",
  wa_group_deleted: "x",
  participant_added: "userplus",
  participant_removed: "userx",
  invite_sent: "send",
  template_sent: "msg",
  media_sent: "img",
  staff_message: "chat",
  customer_message: "chat",
  document_sent: "doc",
  group_updated: "wrench",
  job_card_sent: "doc",
  pre_delivery_feedback_sent: "star",
  post_delivery_feedback_sent: "star",
  vehicle_delivered: "car",
  sla_rung_fired: "bell",
  complaint_confirmation_received: "help",
  complaint_registered: "warn",
  staff_message_blocked: "shield",
};

export function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

export function formatDay(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

export function detailText(ev) {
  const d = ev.event_detail;
  if (!d || typeof d !== "object") return "";
  const parts = [];

  // Who: prefer a real name, fall back to role, always show the number when
  // we have one — the fullest identification the underlying data supports.
  if (d.name || d.role || d.wa_id) {
    let who = d.name || (d.role ? d.role[0].toUpperCase() + d.role.slice(1) : "");
    if (d.role && d.name) who += ` (${d.role})`;
    if (d.wa_id) who += who ? ` · +${d.wa_id}` : `+${d.wa_id}`;
    parts.push(who);
  }
  if (d.template) parts.push(`template: ${d.template}`);
  if (d.stage) parts.push(`stage: ${d.stage}`);
  if (typeof d.file_count === "number") parts.push(`${d.file_count} file${d.file_count === 1 ? "" : "s"}`);
  if (Array.isArray(d.fields) && d.fields.length) parts.push(`changed: ${d.fields.join(", ")}`);
  if (d.reason) parts.push(`reason: ${d.reason}`);
  return parts.join(" · ");
}

function mediaLinks(ev) {
  const d = ev.event_detail;
  if (!d || typeof d !== "object") return [];
  if (Array.isArray(d.media_urls) && d.media_urls.length) return d.media_urls;
  if (d.media_url) return [d.media_url];
  return [];
}

/**
 * The audit trail for one group: every group_events row, oldest first, as the
 * centred system lines of the prototype's record view (.hx-sys), with the
 * event's detail, quoted message and attachments under its line.
 */
export function ActivityTimeline({ events, isLoading, isError, onRetry }) {
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-10">
        <Loader2 size={18} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-10 text-center px-6">
        <h4 className="text-sm font-semibold text-red-700">Could not load group activity</h4>
        <p className="mt-1 max-w-xs text-xs text-slate-500">Check the connection and try loading the activity again.</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  if (!events?.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-msd-soft text-msd-faint">
          <Icon name="info" size={18} />
        </div>
        <h4 className="m-0 text-[15px] font-semibold text-msd-ink">No activity yet</h4>
        <p className="mt-1 max-w-xs text-[13px] text-msd-muted">
          Group creation, joins, sends and updates will show up here as they happen.
        </p>
      </div>
    );
  }

  let lastDay = null;

  return (
    <div className="flex flex-1 flex-col overflow-y-auto px-1 pb-2">
      {events.map((ev) => {
        const { label } = labelForEvent(ev.event_type);
        const day = formatDay(ev.occurred_at);
        const showDaySep = day !== lastDay;
        lastDay = day;
        const detail = detailText(ev);
        const messageText = ev.event_detail?.message_text;
        const media = mediaLinks(ev);
        return (
          <div key={ev.id} className="flex flex-col">
            {showDaySep && (
              <div className="mb-[6px] mt-[18px] self-center text-center text-[12px] font-semibold text-msd-ink2">{day}</div>
            )}
            <div className="my-2 flex max-w-full flex-col items-center gap-[2px] self-center text-center text-[13px] text-msd-ink2">
              <div className="flex flex-wrap items-center justify-center gap-[7px]">
                <Icon
                  name={EVENT_ICON[ev.event_type] || "info"}
                  size={14}
                  stroke={2}
                  color={ACTOR_COLOR[ev.actor_type] || "currentColor"}
                  className={ACTOR_COLOR[ev.actor_type] ? "shrink-0" : "shrink-0 text-msd-faint"}
                />
                <span className="break-words">
                  {label}
                  {ev.actor_name && <span> · {ev.actor_name}</span>}
                </span>
                <span className="tabular-nums text-msd-muted">{formatTime(ev.occurred_at)}</span>
              </div>
              {detail && <div className="max-w-full break-words text-[12.5px] text-msd-muted">{detail}</div>}
              {messageText && (
                <div className="max-w-full truncate text-[12.5px] text-msd-muted" title={messageText}>
                  &ldquo;{messageText}&rdquo;
                </div>
              )}
              {media.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 text-[12.5px]">
                  {media.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="font-semibold text-msd-royal hover:underline">
                      view attachment{media.length > 1 ? ` ${i + 1}` : ""} ↗
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
